import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import * as Tooltip from '@radix-ui/react-tooltip';
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import Brand from '@/components/Brand';
import { useAuth } from '@/context/AuthContext';
import { ADMIN_NAV, CUSTOMER_NAV } from './nav';

/**
 * Primary navigation: labeled and grouped by job (Monitor, Investigate,
 * Exposure, Workspace). Desktop can collapse it to a 56px icon rail; the
 * labels stay in the DOM for screen readers and appear as tooltips on hover
 * and focus. Below 768px it is an off-canvas drawer opened from the top bar.
 */
function RailLink({ item, collapsed, onNavigate }) {
  const Icon = item.icon;
  const link = (
    <NavLink to={item.to} className="sh-nav-link" onClick={onNavigate} data-testid={`nav-${item.to.replace(/\//g, '-').slice(1)}`}>
      <Icon className="sh-ic" aria-hidden="true" />
      <span className={collapsed ? 'sr-only' : 'sh-nav-label'}>{item.label}</span>
    </NavLink>
  );
  if (!collapsed) return link;
  return (
    <Tooltip.Root delayDuration={150}>
      <Tooltip.Trigger asChild>{link}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content side="right" sideOffset={10} className="sh-tooltip">{item.label}</Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export default function Sidebar({ variant = 'customer', collapsed = false, onToggleCollapsed, mobileOpen = false, onNavigate }) {
  const isAdmin = variant === 'admin';
  const { profile } = useAuth();
  const nav = isAdmin ? ADMIN_NAV : CUSTOMER_NAV;
  const org = profile?.company_name;
  const orgMeta = [profile?.industry, profile?.region].filter(Boolean).join(' · ');

  return (
    <Tooltip.Provider>
      <aside className={`sh-side ${collapsed ? 'is-collapsed' : ''} ${mobileOpen ? 'is-open' : ''}`} aria-label="Sidebar">
        <Link to={isAdmin ? '/admin/dashboard' : '/home'} className="sh-brand" aria-label={isAdmin ? 'IntelliSecure admin home' : 'IntelliSecure home'} onClick={onNavigate}>
          <Brand size={22} tone="accent" title="" />
          <span className={collapsed ? 'sr-only' : 'sh-brand-name'}>IntelliSecure</span>
          {isAdmin && !collapsed && <span className="sh-admin-badge">Admin</span>}
        </Link>

        {!isAdmin && org && (
          <Link to="/settings" className="sh-org" title={`${org}${orgMeta ? `, ${orgMeta}` : ''}`} onClick={onNavigate}>
            <span className="sh-org-mark" aria-hidden="true">{org.slice(0, 1).toUpperCase()}</span>
            <span className={collapsed ? 'sr-only' : 'sh-org-text'}>
              <b>{org}</b>
              {orgMeta && <span>{orgMeta}</span>}
            </span>
          </Link>
        )}

        <nav className="sh-nav" aria-label="Primary">
          {nav.map((g, gi) => (
            <div className="sh-nav-group" key={g.group || gi} role="group" aria-label={g.group || 'Main'}>
              {g.group && (collapsed ? <div className="sh-nav-gap" aria-hidden="true" /> : <div className="sh-nav-heading">{g.group}</div>)}
              {g.items.map((item) => <RailLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />)}
            </div>
          ))}
        </nav>

        {onToggleCollapsed && (
          <div className="sh-side-foot">
            <button
              type="button"
              className="sh-icon-btn"
              onClick={onToggleCollapsed}
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? <PanelLeftOpen className="sh-ic" aria-hidden="true" /> : <PanelLeftClose className="sh-ic" aria-hidden="true" />}
            </button>
          </div>
        )}
      </aside>
    </Tooltip.Provider>
  );
}
